import { visitTranscriptCreateSchema } from "@rosc/contracts";
import { z } from "zod";
import { createAudioRecording } from "@/services/audio-uploads";
import {
  assertNoObsoleteSubmission,
  discardObsoleteSubmissions,
} from "./obsolete-submissions";
import {
  prepareOwnedAudioFile,
  uploadOwnedAudioFile,
} from "./owned-audio-upload";

const settingsSchema = z.strictObject({
  duration: visitTranscriptCreateSchema.shape.duration,
  language: visitTranscriptCreateSchema.shape.language,
  // The live transcript of this recording, when there is one: sent with the
  // uploaded audio, which is then kept for playback (not transcribed again).
  transcription: visitTranscriptCreateSchema.shape.transcription.optional(),
  recordingConsent: z.literal(true).optional(),
});
const uuid = z.uuid().toLowerCase();
const identity = {
  version: z.literal(2),
  uploadKey: uuid,
  recordingKey: uuid,
  fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  settings: settingsSchema,
};
const schema = z.discriminatedUnion("kind", [
  z.strictObject({ ...identity, kind: z.literal("intent") }),
  z.strictObject({ ...identity, kind: z.literal("upload"), uploadId: uuid }),
  z.strictObject({ ...identity, kind: z.literal("accepting"), uploadId: uuid }),
  z.strictObject({
    ...identity,
    kind: z.literal("accepted"),
    uploadId: uuid,
    recordingPublicId: z.uuidv4().toLowerCase(),
  }),
]);
export type AudioRecordingSubmission = z.output<typeof schema>;
const storageKey = (account: string, parent: string) =>
  `rosc:audio-recording-submission:${encodeURIComponent(account)}:${parent}`;
export const readAudioRecordingSubmission = (
  account: string,
  parent: string,
): AudioRecordingSubmission | null => {
  assertNoObsoleteSubmission(account, "rosc:audio-recording-submission:");
  try {
    const value = sessionStorage.getItem(storageKey(account, parent));
    return value === null ? null : schema.parse(JSON.parse(value));
  } catch {
    throw Error(
      "Saved audio recording cannot be read. Reselect this visit before abandoning it.",
    );
  }
};
const save = (
  account: string,
  parent: string,
  value: AudioRecordingSubmission,
) => {
  try {
    sessionStorage.setItem(
      storageKey(account, parent),
      JSON.stringify(schema.parse(value)),
    );
  } catch {
    throw Error(
      "Recording retry identity could not be saved. Reselect this visit before trying again.",
    );
  }
};
const transition = (
  account: string,
  parent: string,
  expected: AudioRecordingSubmission,
  next: AudioRecordingSubmission,
) => {
  if (
    JSON.stringify(readAudioRecordingSubmission(account, parent)) !==
    JSON.stringify(expected)
  )
    throw Error(
      "This audio recording is no longer active. Reselect this visit before trying again.",
    );
  save(account, parent, next);
};
export const prepareAudioRecordingFile = async ({
  file,
  settings,
  signal,
}: {
  file: Blob;
  settings: z.input<typeof settingsSchema>;
  signal?: AbortSignal;
}) => {
  const body = settingsSchema.parse(settings);
  const prepared = await prepareOwnedAudioFile(file, signal);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(
      JSON.stringify([
        "audio-recording-v1",
        prepared.input.contentType,
        prepared.input.byteLength,
        prepared.input.checksumSha256,
        body.duration,
        body.language,
        // Appended only when present, so earlier fingerprints still match.
        ...(body.transcription === undefined
          ? []
          : [body.transcription, body.recordingConsent ?? null]),
      ]),
    ),
  );
  signal?.throwIfAborted();
  const fingerprint = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return { ...prepared, body, fingerprint };
};
export type PreparedAudioRecording = Awaited<
  ReturnType<typeof prepareAudioRecordingFile>
>;
export const prepareAudioRecordingSubmission = (
  account: string,
  parent: string,
  prepared: PreparedAudioRecording,
): AudioRecordingSubmission => {
  const existing = readAudioRecordingSubmission(account, parent);
  if (existing) {
    if (existing.fingerprint !== prepared.fingerprint)
      throw Error(
        "This audio recording has changed. Reselect the same file/settings or check this visit before abandoning it.",
      );
    return existing;
  }
  const state: AudioRecordingSubmission = {
    version: 2,
    kind: "intent",
    uploadKey: crypto.randomUUID(),
    recordingKey: crypto.randomUUID(),
    fingerprint: prepared.fingerprint,
    settings: prepared.body,
  };
  save(account, parent, state);
  return state;
};
export const abandonAudioRecordingSubmission = (
  account: string,
  parent: string,
): void => {
  discardObsoleteSubmissions(account, "rosc:audio-recording-submission:");
  try {
    sessionStorage.removeItem(storageKey(account, parent));
  } catch {
    throw Error(
      "Recording retry identity could not be removed. Reselect this visit before trying again.",
    );
  }
};
export const beginAudioRecordingDraft = (
  account: string,
  parent: string,
): void => {
  if (readAudioRecordingSubmission(account, parent)?.kind === "accepted")
    abandonAudioRecordingSubmission(account, parent);
};
export const runAudioRecordingUpload = async ({
  account,
  parent,
  recordingKey,
  prepared,
  userUid,
  signal,
  isCurrent,
}: {
  account: string;
  parent: string;
  recordingKey: string;
  prepared?: PreparedAudioRecording;
  userUid: string;
  signal: AbortSignal;
  isCurrent: () => boolean;
}): Promise<{ public_id: string }> => {
  const check = () => {
    signal.throwIfAborted();
    if (!isCurrent())
      throw new DOMException("Recording session changed", "AbortError");
  };
  check();
  let state = readAudioRecordingSubmission(account, parent);
  if (
    !state ||
    state.recordingKey !== recordingKey ||
    (prepared && state.fingerprint !== prepared.fingerprint)
  )
    throw Error(
      "This audio recording is no longer active. Reselect this visit before trying again.",
    );
  if (state.kind === "accepted") return { public_id: state.recordingPublicId };
  const identity = { version: z.literal(2), userUid, signal };
  if (state.kind === "intent" || state.kind === "upload") {
    if (!prepared)
      throw Error("Reselect the same audio file to finish this upload.");
    let ready = state;
    await uploadOwnedAudioFile({
      file: prepared.file,
      input: prepared.input,
      identity: { ...identity, key: state.uploadKey },
      uploadId: state.kind === "upload" ? state.uploadId : undefined,
      onCreated: (id) => {
        if (ready.kind === "intent") {
          const next = schema.parse({
            ...ready,
            version: 2,
            kind: "upload",
            uploadId: id,
          });
          if (next.kind !== "upload") throw Error("Invalid upload identity");
          transition(account, parent, ready, next);
          ready = next;
        } else if (ready.uploadId !== id)
          throw Error("Upload recovery identity changed");
      },
      isCurrent,
    });
    check();
    if (ready.kind !== "upload") throw Error("Upload identity was not saved");
    const next = {
      ...ready,
      version: 2,
      kind: "accepting",
    } satisfies AudioRecordingSubmission;
    transition(account, parent, ready, next);
    state = next;
  }
  check();
  const { transcription, recordingConsent, ...settings } = state.settings;
  const result = await createAudioRecording(
    parent,
    transcription === undefined
      ? { ...settings, audioUploadId: state.uploadId }
      : {
          ...settings,
          name: state.recordingKey,
          transcription,
          ...(recordingConsent ? { recordingConsent } : {}),
          audioUploadId: state.uploadId,
        },
    { ...identity, key: state.recordingKey },
  );
  check();
  transition(account, parent, state, {
    ...state,
    version: 2,
    kind: "accepted",
    recordingPublicId: result.public_id,
  });
  return result;
};
